import {
    Paper,
    TextField,
    MenuItem,
    Button,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
} from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useEffect, useState, type FC } from 'react'
import { useParams } from 'react-router-dom'
import { useOrganization } from '../../../../stores/organization-store'
import type { Stores } from '../../../../util/store'

const CNH_STOREFRONTS = [
    'es/es',
    'us/en',
    'ca/en',
    'gb/en',
    'fr/fr',
    'de/de',
    'it/it',
    'ie/en',
    'at/de',
    'pt/pt',
    'eu/en',
    'amea/en',
    'sa/en',
    'nz/en',
    'au/en',
    'anz/en',
]

interface FeatureFlag {
    name: string
    label: string
    description: string
    // What the apps do when the organization has no row for this flag at all.
    // Story writer is opt-out (already live for everyone, so an absent row must
    // not hide it); web search is opt-in.
    defaultEnabled: boolean
}

const FEATURE_FLAGS: FeatureFlag[] = [
    {
        name: 'story_writer_enabled',
        label: 'StoryWriter Assistant™',
        description:
            'Show the Research / StoryWriter switch in the sidebar. On unless explicitly turned off.',
        defaultEnabled: true,
    },
    {
        name: 'web_search_enabled',
        label: 'Web search',
        description:
            'Let this organization search external web sources from chat.',
        defaultEnabled: false,
    },
    {
        name: 'cnh_parts_enabled',
        label: 'CNH Parts Lookup',
        description:
            'Let this organization look up serial-number parts in the official CNH catalog from chat.',
        defaultEnabled: false,
    },
    {
        name: 'internal_document_upload_admin_only',
        label: 'Admin-only internal document uploads',
        description:
            'Restrict uploading internal documents to organization admins. Off unless explicitly turned on.',
        defaultEnabled: false,
    },
    {
        name: 'chat_image_input_enabled',
        label: 'Pictures in chat',
        description:
            'Let this organization attach a picture to a chat question, for Vi to look at. Off unless explicitly turned on.',
        defaultEnabled: false,
    },
]

export interface OrganizationFeatureFlagsPageProps {
    stores: Stores
}

export const OrganizationFeatureFlagsPage: FC<OrganizationFeatureFlagsPageProps> =
    observer(({ stores }) => {
        const { organizationId } = useParams()
        const organization = useOrganization(
            stores.organizations,
            organizationId,
        )

        const savedRegion = organization?.cnh_parts_region
        const [region, setRegion] = useState('au')
        useEffect(() => {
            if (savedRegion !== undefined) setRegion(savedRegion)
        }, [organizationId, savedRegion])

        if (!organizationId || !organization) return null

        return (
            <TableContainer
                component={Paper}
                data-testid="org-feature-flags-page"
                style={{ marginTop: '1rem' }}>
                <Table>
                    <TableBody>
                        {FEATURE_FLAGS.map((flag) => {
                            const enabled =
                                organization.feature_flags?.[flag.name] ??
                                flag.defaultEnabled

                            return (
                                <TableRow key={flag.name}>
                                    <TableCell component="th" scope="row">
                                        <div>{flag.label}</div>
                                        <div className="color-dimmed">
                                            {flag.description}
                                        </div>
                                        {flag.name === 'cnh_parts_enabled' && (
                                            <div>
                                                <TextField
                                                    select
                                                    label="CNH region / language"
                                                    value={region}
                                                    size="small"
                                                    margin="normal"
                                                    helperText="CNH storefront language only; answers keep their usual language."
                                                    disabled={
                                                        stores.organizations
                                                            .isUpdating
                                                    }
                                                    onChange={(event) =>
                                                        setRegion(
                                                            event.target.value,
                                                        )
                                                    }>
                                                    {CNH_STOREFRONTS.map(
                                                        (pair) => (
                                                            <MenuItem
                                                                key={pair}
                                                                value={
                                                                    pair.split(
                                                                        '/',
                                                                    )[0]
                                                                }>
                                                                {pair}
                                                            </MenuItem>
                                                        ),
                                                    )}
                                                </TextField>
                                                <Button
                                                    disabled={
                                                        stores.organizations
                                                            .isUpdating ||
                                                        region ===
                                                            organization.cnh_parts_region
                                                    }
                                                    onClick={() =>
                                                        void stores.organizations.setCnhPartsRegion(
                                                            organizationId,
                                                            region,
                                                        )
                                                    }>
                                                    Save storefront
                                                </Button>
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Switch
                                            checked={enabled}
                                            color="success"
                                            disabled={
                                                stores.organizations.isUpdating
                                            }
                                            edge="start"
                                            onChange={() => {
                                                void stores.organizations.setFeatureFlag(
                                                    organizationId,
                                                    flag.name,
                                                    !enabled,
                                                )
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        )
    })
